import React, { useState } from 'react';
import { Clock, Bell, BellRing, Check } from 'lucide-react';
import { Button } from './ui/button';
import { formatDateGB } from '../lib/dates';
import { leaveApi } from '../lib/api';
import { toast } from 'sonner';
import { bookingLength, withTimes } from '../lib/bookingTime';

/**
 * "Your pending requests" — the list of leave YOU are waiting on a decision for.
 *
 * WHY THIS IS A SHARED COMPONENT (01/08/2026). It lived inline on the dashboard and
 * nowhere else, which meant My Leave — the page named after your leave — showed your
 * pending requests only as dashed cells on the year planner. You couldn't see how many
 * were waiting, who they were with, or which step they'd reached. Rob spotted it:
 * "I'm not sure where the pending requests show on the My Leave page, it is not
 * obvious." It wasn't obvious because there wasn't one.
 *
 * Extracting it also means the reminder panel below is written once rather than twice.
 */

/** "3 Aug" — Rob's call (A3): three dates in one sentence, so the short form. */
function shortDate(iso) {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });
}

/**
 * A label precise enough to be distinct.
 *
 * The ladder is configured in HOURS and a superadmin may set it as low as 1, so three
 * reminders can land on the same day — at which point date-only formatting produces
 * "on 31 Jul, 31 Jul and 31 Jul" (Rob, 02/08/2026). When everything is inside a day,
 * the time is the only thing that distinguishes them.
 */
function scheduleLabel(iso) {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  const now = new Date();
  const hoursAway = (d - now) / 3600000;
  const time = d.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' });
  const sameDay = d.toDateString() === now.toDateString();
  if (sameDay) return `today at ${time}`;
  if (hoursAway < 36) return `tomorrow at ${time}`;
  return shortDate(iso);
}

/** Today / tomorrow read far better than a date when the thing is imminent — and
 *  imminent is exactly when someone is deciding whether to spend their one nudge. */
function whenWord(iso) {
  const d = shortDate(iso);
  if (!d) return null;
  const then = new Date(iso);
  const now = new Date();
  const days = Math.round((then.setHours(0, 0, 0, 0) - now.setHours(0, 0, 0, 0)) / 86400000);
  if (days <= 0) return 'today';
  if (days === 1) return 'tomorrow';
  return d;
}

const listOut = (parts) => {
  const items = parts.filter(Boolean);
  if (items.length <= 1) return items[0] || '';
  return `${items.slice(0, -1).join(', ')} and ${items[items.length - 1]}`;
};

/**
 * The sentence under the button. Every location is a real state the ladder can be in —
 * see services/approval_reminders.nudge_state, which supplies the facts. This function
 * only chooses words; it never works out a date, so the screen cannot promise a
 * schedule the scheduler won't honour.
 */
/**
 * "…on 3 Aug and 5 Aug", or "shortly" for anything whose moment has already passed.
 *
 * ⚠️ An unsent reminder can be OVERDUE — the job runs hourly and catches up on its next
 * tick. Printing its scheduled time then tells the employee they'll be reminded on a
 * date that's already gone. `overdue_reminders` from the backend says how many are in
 * that state; they collapse into a single "shortly" rather than repeating it.
 */
function scheduleClause(times, overdueCount) {
  const future = (times || []).slice(overdueCount || 0).map(scheduleLabel).filter(Boolean);

  // "tomorrow at 00:25, tomorrow at 01:25 and tomorrow at 02:25" is what a machine
  // says. Once a day has been named, the following times belong to it until the day
  // changes — so drop the repeat and let it read "tomorrow at 00:25, 01:25 and 02:25".
  let lastPrefix = null;
  const tidied = future.map((label) => {
    const m = /^(today|tomorrow) at (.+)$/.exec(label);
    if (!m) { lastPrefix = null; return label; }
    const [, prefix, time] = m;
    if (prefix === lastPrefix) return time;
    lastPrefix = prefix;
    return label;
  });

  const parts = [];
  if (overdueCount > 0) parts.push('shortly');
  parts.push(...tidied);
  if (parts.length === 0) return '';
  return listOut(parts);
}

function reminderSentence(n, approver) {
  const who = approver || 'your approver';
  const admins = n.escalates_at
    ? (n.escalation_overdue
        ? ' If it\'s still unanswered it goes to your admins shortly.'
        : ` If it's still unanswered it goes to your admins ${scheduleLabel(n.escalates_at)}.`)
    : '';
  const schedule = scheduleClause(n.upcoming_reminders, n.overdue_reminders);

  if (n.already_nudged) {
    const rest = schedule
      ? ` ${who} will still be reminded automatically ${schedule}.`
      : '';
    return `Reminder sent on ${shortDate(n.nudged_at)} — one per request.${rest}${admins}`;
  }

  if (n.escalated) {
    return 'This is now with your admins. You can still send one reminder.';
  }

  if (!n.ladder_enabled) {
    return 'You can send one per request. There are no automatic reminders, so this is the only nudge available.';
  }

  // Mid-ladder: saying they've ALREADY been chased slightly undercuts the button, on
  // purpose. It's the honest signal that the system is working and the hold-up is
  // human — which may send the employee for a quiet word instead of another email.
  if (n.reminders_sent > 0) {
    const times = n.reminders_sent === 1 ? 'once' : n.reminders_sent === 2 ? 'twice' : `${n.reminders_sent} times`;
    const next = n.next_reminder_at
      ? ` They'll be reminded again ${n.overdue_reminders > 0 ? 'shortly' : whenWord(n.next_reminder_at)}.`
      : ' No further reminders are scheduled.';
    return `${who} has already been reminded ${times}.${next}${admins} You haven't used your reminder yet.`;
  }

  const upcoming = schedule
    ? ` ${who} will also be reminded automatically ${schedule}.`
    : '';
  return `You can send one per request.${upcoming}${admins}`;
}

const ReminderPanel = ({ req, onSent }) => {
  const n = req.nudge;
  const [sending, setSending] = useState(false);
  if (!n || !n.nudge_enabled) return null;

  const approver = n.approver_name;

  // Too soon. Shown rather than hidden so nobody wonders whether the feature exists.
  if (!n.can_nudge && !n.already_nudged) {
    return (
      <p className="mt-3 pt-3 border-t border-border text-sm text-muted-foreground flex items-center gap-2">
        <Clock className="w-4 h-4 flex-shrink-0" />
        You can send {approver ? `${approver} ` : ''}a reminder from{' '}
        {whenWord(n.available_at) || 'tomorrow'}.
      </p>
    );
  }

  if (n.already_nudged) {
    return (
      <p className="mt-3 pt-3 border-t border-border text-sm text-muted-foreground flex items-start gap-2 leading-relaxed">
        <Check className="w-4 h-4 flex-shrink-0 mt-0.5 text-emerald-600" />
        <span>{reminderSentence(n, approver)}</span>
      </p>
    );
  }

  const send = async () => {
    setSending(true);
    try {
      await leaveApi.nudge(req.id);
      toast.success('Reminder sent');
      if (onSent) onSent();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not send the reminder');
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="mt-3 pt-3 border-t border-border flex items-start gap-3 flex-wrap">
      <Button
        size="sm"
        variant="outline"
        onClick={send}
        disabled={sending}
        data-testid={`nudge-${req.id}`}
        className="whitespace-nowrap"
      >
        <BellRing className="w-4 h-4 mr-1.5" />
        {sending ? 'Sending…' : 'Send a reminder'}
      </Button>
      <p className="text-sm text-muted-foreground flex-1 min-w-[220px] leading-relaxed">
        {reminderSentence(n, approver)}
      </p>
    </div>
  );
};

const YourPendingRequests = ({ requests, userId, onCancel, onChanged, className = '' }) => {
  const mine = (requests || []).filter(
    (r) => String(r.user_id) === String(userId) && r.status === 'pending');
  if (mine.length === 0) return null;

  return (
    <div className={`card-hive p-5 sm:p-6 ${className}`} data-testid="your-pending-requests">
      <h2 className="font-heading text-lg font-semibold mb-4 flex items-center gap-2">
        <Clock className="w-5 h-5 text-hive-orange" /> Your pending requests
      </h2>
      <div className="space-y-3">
        {mine.map((r) => {
          const pend = (r.approval_chain || []).find(
            (a) => (a.status || '').toLowerCase() === 'pending');
          const withWho = pend && pend.expected_approver_name;
          return (
            <div key={r.id} className="px-4 py-3 bg-background dark:bg-secondary rounded-[14px]">
              <div className="flex items-start justify-between gap-4">
                <div className="flex items-start gap-3 min-w-0">
                  <div className="w-1 self-stretch min-h-[2.5rem] rounded-full flex-shrink-0 opacity-60"
                       style={{ backgroundColor: r.leave_type_color }} />
                  <div className="min-w-0">
                    <p className="font-bold">
                      {r.leave_type_name} • {withTimes(formatDateGB(r.start_date), r)}
                      {r.start_date !== r.end_date ? ` – ${formatDateGB(r.end_date)}` : ''}
                      {' '}({bookingLength(r)})
                    </p>
                    <p className="text-sm text-muted-foreground">
                      {withWho ? `Awaiting ${withWho}` : 'Awaiting approval'}
                      {r.total_approval_levels > 1
                        ? ` • Step ${r.current_approval_level} of ${r.total_approval_levels}`
                        : ''}
                    </p>
                    {r.reason && (
                      <p className="text-sm text-foreground/80 mt-1 italic">&ldquo;{r.reason}&rdquo;</p>
                    )}
                  </div>
                </div>
                {onCancel && (
                  <Button size="sm" variant="outline" className="bg-card hover:bg-hive-danger-bg hover:text-hive-danger-fg hover:border-hive-danger-fg/40 dark:hover:bg-red-500/15 dark:hover:text-red-300" onClick={() => onCancel(r.id)}>Cancel</Button>
                )}
              </div>
              <ReminderPanel req={r} onSent={onChanged} />
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default YourPendingRequests;
